/**
 * Marketing Flex Table — grid to CSV and to the Excel export payload.
 *
 * Both exports carry every row the current setup produces (search, sort,
 * dimensions, view and metric selection applied) whatever is collapsed on
 * screen (R26/R27): they read `grid.exportRows`, never `grid.rows`.
 */

import { defuseFormula, toCsv } from '@/lib/csv';
import type { MetricFormat } from './flexCatalog';
import type { FlexGrid, FlexGridRow } from './pivot';

export const REPORT_TITLE = 'Marketing Flex Table';

export interface FlexExportFilter {
  label: string;
  values: string[];
}

export interface FlexExportContext {
  period: { from: string; to: string };
  /** Active filters only; each becomes one preamble line. */
  filters: FlexExportFilter[];
}

export type FlexExcelFormat = 'text' | MetricFormat;

/** The body the server's Excel endpoint accepts. Currency is cents, percent a fraction. */
export interface FlexExcelPayload {
  title: string;
  periodLabel: string;
  filterLines: string[];
  columns: { label: string; format: FlexExcelFormat }[];
  rows: { level: 0 | 1; cells: (string | number | null)[] }[];
}

function periodLabel(period: FlexExportContext['period']): string {
  return `${period.from} to ${period.to}`;
}

function allRows(grid: FlexGrid): FlexGridRow[] {
  return [grid.totals, ...grid.exportRows];
}

function isNested(grid: FlexGrid): boolean {
  return grid.exportRows.some((r) => r.level === 1);
}

/** Plain CSV numbers: no currency symbol or thousands separators. */
export function formatCsvMetric(value: number | null, format: MetricFormat): string {
  if (value === null || !Number.isFinite(value)) return '';
  if (format === 'currency') return (value / 100).toFixed(2);
  if (format === 'percent') return `${(value * 100).toFixed(1)}%`;
  return String(Math.round(value));
}

/**
 * The CSV as rows. Text cells are defused (a leading = + - @ tab or CR gets a
 * quote) because UTM values come from public lead forms; metric cells are
 * numbers formatted here and are left alone.
 */
export function buildCsvRows(grid: FlexGrid, context: FlexExportContext): string[][] {
  const nested = isNested(grid);
  const text = (value: string) => defuseFormula(value);
  const metricColumns = grid.columns.filter((c) => c.kind === 'metric');

  const out: string[][] = [
    [text(REPORT_TITLE)],
    ['Period', text(periodLabel(context.period))],
    ...context.filters.map((f) => [text(f.label), text(f.values.join(', '))]),
    [''],
  ];

  out.push([...(nested ? ['Level'] : []), ...grid.columns.map((c) => text(c.label))]);

  for (const row of allRows(grid)) {
    const level = row.isTotal ? 'Total' : row.level === 1 ? 'Child' : 'Parent';
    const labels = row.labels.map((label) => (row.level === 1 ? `  ${text(label)}` : text(label)));
    const values = row.values.map((v, i) => {
      const format = metricColumns[i]?.format;
      return formatCsvMetric(v, format === undefined || format === 'text' ? 'number' : format);
    });
    out.push([...(nested ? [level] : []), ...labels, ...values]);
  }
  return out;
}

export function buildCsv(grid: FlexGrid, context: FlexExportContext): string {
  return toCsv(buildCsvRows(grid, context));
}

/** Cells are typed by the server, so labels go through untouched here. */
export function buildExcelPayload(grid: FlexGrid, context: FlexExportContext): FlexExcelPayload {
  return {
    title: REPORT_TITLE,
    periodLabel: periodLabel(context.period),
    filterLines: context.filters.map((f) => `${f.label}: ${f.values.join(', ')}`),
    columns: grid.columns.map((c) => ({ label: c.label, format: c.format })),
    rows: allRows(grid).map((row) => ({ level: row.level, cells: [...row.labels, ...row.values] })),
  };
}

/** `Marketing Flex Table <from> to <to>.<ext>`, with file-name-unsafe characters replaced. */
export function exportFileName(period: FlexExportContext['period'], ext: 'csv' | 'xlsx'): string {
  // eslint-disable-next-line no-control-regex
  const safe = (s: string) => s.replace(/[\\/:*?"<>|\u0000-\u001f]/g, '-').trim();
  return `${REPORT_TITLE} ${safe(period.from)} to ${safe(period.to)}.${ext}`;
}
